import axios from "axios";
import Cookies from "js-cookie";
import { firebaseAPI } from "../firebaseConfig";
import axiosInstance from "../utils/axiosInstance";

const authHeader = () => ({
  Authorization: `Bearer ${Cookies.get("token") || ""}`,
});

export const fetchSubmissions = (formName, programName, params) =>
  axiosInstance.get(
    `${firebaseAPI}/forms/${formName}/${programName}/submissions`,
    {
      headers: authHeader(),
      params,
    }
  );

export const searchSubmissions = (formName, programName, params) =>
  axiosInstance.get(
    `${firebaseAPI}/forms/${formName}/${programName}/submissions/search`,
    {
      headers: authHeader(),
      params,
    }
  );

export const fetchAllSubmissions = (formName, programName) =>
  axiosInstance.get(
    `${firebaseAPI}/forms/${formName}/${programName}/submissions/all`,
    {
      headers: authHeader(),
    }
  );

export const updateSubmission = (formName, programName, submissionId) =>
  axiosInstance.get(`${firebaseAPI}/submissions/${submissionId}`, {
    headers: authHeader(),
  });

export const changeStatus = (action, payload) =>
  axiosInstance.post(`${firebaseAPI}/${action}`, payload, {
    headers: authHeader(),
  });

export const fetchJudges = (programName) =>
  axiosInstance.get(`${firebaseAPI}/users/${programName}/judges`, {
    headers: authHeader(),
  });

export const fetchRounds = (programName) =>
  axiosInstance.get(`${firebaseAPI}/program/${programName}/rounds`, {
    headers: authHeader(),
  });

export const assignJudge = (submissionId, judgeEmail) =>
  axiosInstance.post(
    `${firebaseAPI}/submissions/assignJudge`,
    { submissionId, judgeEmail },
    { headers: authHeader() }
  );

/**
 * Bulk change status of participants based on filters
 * @param {Object} params
 * @param {string} params.programName
 * @param {string} params.formName
 * @param {string} params.newStatus
 * @param {Object} params.filters
 */
export const bulkChangeStatus = async ({
  programName,
  formName,
  newStatus,
  filters,
}) => {
  const response = await axiosInstance.post(
    `${firebaseAPI}/bulk-change-status`,
    {
      programName,
      formName,
      newStatus,
      filters,
    },
    {
      headers: authHeader(),
    }
  );
  return response.data;
};

/**
 * Get count of participants matching filters
 * @param {Object} params
 * @param {string} params.programName
 * @param {string} params.formName
 * @param {Object} params.filters
 */
export const countMatchingParticipants = async ({
  programName,
  formName,
  filters,
}) => {
  const response = await axiosInstance.get(`${firebaseAPI}/count-matching`, {
    params: {
      programName,
      formName,
      ...filters,
    },
    headers: authHeader(),
  });
  return response.data.count;
};

export const fetchFilteredRoundscores = async ({
  programName,
  formName,
  filters = {},
  page = 1,
  limit = 20,
}) => {
  try {
    const token = Cookies.get("token") || "";

    const response = await axiosInstance.get(
      `${firebaseAPI}/forms/${formName}/${programName}/submissions`,
      {
        headers: authHeader(),
        params: {
          page,
          limit,
          ...filters,
        },
      }
    );

    const submissions = (response.data?.submissions ?? []).map(
      (submission) => ({
        ...submission,
        submissionId:
          submission.submissionId ||
          `placeholder-${Math.floor(Math.random() * 1e9)}`,
        judgeScore: submission.judgeScore || 0,
        judgesAndScores: submission.judgesAndScores || [],
      })
    );

    return {
      submissions,
      totalPages: response.data?.totalPages ?? 0,
    };
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error.message ||
        "Failed to fetch submissions"
    );
  }
};

export const fetchContactSearchSubmissions = async ({
  programName,
  formName,
  email,
  phone,
}) => {
  try {
    const token = Cookies.get("token") || "";

    if (!email && !phone) {
      throw new Error("Email or phone must be provided.");
    }

    const response = await axiosInstance.get(
      `${firebaseAPI}/forms/${formName}/${programName}/submissions/contact`,
      {
        headers: authHeader(),
        params: {
          email,
          phone,
        },
      }
    );

    const submissions = (response.data?.submissions ?? []).map(
      (submission) => ({
        ...submission,
        submissionId:
          submission.submissionId ||
          `placeholder-${Math.floor(Math.random() * 1e9)}`,
        judgeScore: submission.judgeScore || 0,
        judgesAndScores: submission.judgesAndScores || [],
      })
    );

    return {
      submissions,
      totalPages: response.data?.totalPages ?? 1,
    };
  } catch (error) {
    throw new Error(
      error?.response?.data?.message ||
        error.message ||
        "Failed to fetch submissions by contact"
    );
  }
};

export const bulkChangeParticipantsStatus = async (params) => {
  const { programName, formName, filters, newStatus } = params;

  const payload = {
    formName,
    filters,
    newStatus,
    programName,
  };

  const response = await axiosInstance.post(
    `${firebaseAPI}/participants/status/bulk-update`,
    payload,
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const bulkAssignJudgeToSubmissions = async (params) => {
  const { formName, filters, judgeEmail, programName } = params;

  const payload = {
    formName,
    filters,
    judgeEmail,
    programName,
  };

  const response = await axiosInstance.post(
    `${firebaseAPI}/participants/judge/bulk-update`,
    payload,
    {
      headers: authHeader(),
    }
  );

  return response.data;
};

export const downloadFilteredRoundscoresCSV = async ({
  programName,
  formName,
  filters = {},
}) => {
  try {
    // 1. Get auth token (or throw if missing)
    const token = Cookies.get("token") || "";

    // 2. Construct the URL for the CSV endpoint
    const url = `${firebaseAPI}/program/${programName}/${formName}/csv/submissions`;

    // 3. Issue GET request with filters as query params
    const response = await axiosInstance.get(url, {
      headers: {
        ...authHeader(),
      },
      params: filters,
      responseType: "blob", // so we receive CSV as a Blob
    });

    // 4. Create a Blob URL and trigger browser download
    const blob = new Blob([response.data], { type: "text/csv;charset=utf-8;" });
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    // Suggest a filename (timestamped)
    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-");
    link.setAttribute("download", `form_submissions_${timestamp}.csv`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return true;
  } catch (error) {
    console.error("Failed to download CSV:", error);
    throw new Error(
      error?.response?.data?.message || error?.message || "CSV download failed"
    );
  }
};

export const downloadBatchedEmailsCSV = async ({
  programName,
  formName,
  batchSize = 50,
  filters = {},
  appendEmail,
}) => {
  try {
    const url = `${firebaseAPI}/program/${programName}/${formName}/csv/emails`;

    const response = await axiosInstance.get(url, {
      headers: {
        ...authHeader(),
      },
      params: {
        ...filters,
        batchSize,
        appendEmail,
      },
      responseType: "blob", // 🔥 Treat response as file/blob
    });

    const blob = new Blob([response.data], {
      type: "text/csv;charset=utf-8;",
    });

    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = downloadUrl;

    const timestamp = new Date()
      .toISOString()
      .slice(0, 19)
      .replace(/[:T]/g, "-");

    link.setAttribute(
      "download",
      `batched_emails_bcc_${batchSize}per_${timestamp}.csv`
    );

    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(downloadUrl);

    return true;
  } catch (error) {
    console.error("Failed to download batched emails CSV:", error);
    throw new Error(
      error?.response?.data?.message ||
        error?.message ||
        "Batched emails CSV download failed"
    );
  }
};
